import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationMap = () => {
  const [mapLoaded, setMapLoaded] = useState(false);

  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const handleDirections = () => {
    window.open('https://www.google.com/maps/dir/?api=1&destination=Maayo+Laundry+and+Dry+cleaning', '_blank');
  };

  const handleShareLocation = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Maayo Fabric Care Location',
        text: 'Visit Maayo Fabric Care & Dry Cleaning',
        url: 'https://www.google.com/maps/dir/?api=1&destination=Maayo+Laundry+and+Dry+cleaning'
      });
    } else {
      navigator.clipboard.writeText('https://www.google.com/maps/dir/?api=1&destination=Maayo+Laundry+and+Dry+cleaning');
      alert('Location link copied to clipboard!');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3 }}
      className="bg-card rounded-2xl overflow-hidden shadow-elevation-2"
    >
      {/* Map Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Find Us Here
            </h2>
            <p className="text-muted-foreground">
              Visit our main facility in Coimbatore
            </p>
          </div>
          
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDirections}
              iconName="Navigation"
              iconPosition="left"
              className="transition-smooth hover:hover-scale"
            >
              Directions
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShareLocation}
              iconName="Share"
              className="transition-smooth hover:hover-scale"
            >
              Share
            </Button>
          </div>
        </div>
      </div>
      {/* Map Container */}
      <div className="relative">
        <div className="aspect-video bg-muted/20 relative overflow-hidden">
          {!mapLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
              <div className="text-center">
                <Icon name="MapPin" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">Loading map...</p>
              </div>
            </div>
          )}
          
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31323.261337118307!2d76.94564580917357!3d11.082963917948165!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba8f7ad62a82c93%3A0x9b9bb2cd3fe1f500!2sMaayo%20Laundry%20and%20Dry%20cleaning!5e0!3m2!1sen!2sus!4v1772088152644!5m2!1sen!2sus"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            title="Maayo Fabric Care Location"
            allowFullScreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            onLoad={handleMapLoad}
            className={`transition-opacity duration-500 ${mapLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
        </div>

        {/* Map Overlay Info */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="bg-card/95 backdrop-blur-sm rounded-lg p-4 shadow-elevation-2">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="MapPin" size={20} color="white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground mb-1">
                  Maayo Fabric Care & Dry Cleaning
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  30/1, Tank Street, Velappanaickenpudur,<br />
                  Chinnavedampatti, Coimbatore – 641049
                </p>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDirections}
                iconName="ExternalLink"
                className="flex-shrink-0 transition-smooth hover:hover-scale"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Location Details */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Car" size={24} color="var(--color-success)" />
            </div>
            <h4 className="font-medium text-foreground mb-2">Parking Available</h4>
            <p className="text-sm text-muted-foreground">
              Free customer parking space available
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Clock" size={24} color="var(--color-primary)" />
            </div>
            <h4 className="font-medium text-foreground mb-2">Quick Service</h4>
            <p className="text-sm text-muted-foreground">
              Express counter for urgent requests
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Icon name="Accessibility" size={24} color="var(--color-warning)" />
            </div>
            <h4 className="font-medium text-foreground mb-2">Accessible</h4>
            <p className="text-sm text-muted-foreground">
              Wheelchair accessible entrance
            </p>
          </div>
        </div>

        {/* Transportation Info */}
        <div className="mt-8 pt-6 border-t border-border">
          <h4 className="font-medium text-foreground mb-4">How to Reach</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <Icon name="Bus" size={20} color="var(--color-primary)" className="mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">By Bus</p>
                <p className="text-sm text-muted-foreground">
                  Bus routes 1, 7, 12 stop near Brookefields Mall
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Icon name="Car" size={20} color="var(--color-success)" className="mt-1 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">By Car</p>
                <p className="text-sm text-muted-foreground">
                  15 minutes from Coimbatore Junction
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LocationMap;